// ============================================================================
// BOOKING MODAL — view, edit, review and cancel a studio booking.
// Shared by the Room Calendar and the Studio CRM screen; edit rights are
// enforced here (owner, Tier 1, system admin) and again by the backend.
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  X, User, Tag, Target, Clock, MapPin, Users, Pencil, Check, Mail, RefreshCw, AlertCircle, Building2, Trash2
} from 'lucide-react';
import {
  updateBookingMetadata,
  updateBookingDetails,
  listFacilitators,
  cancelBooking,
  reviewBooking,
  retryOutlookSync,
  StudioBooking,
  BookingStatus,
  FacilitatorSummary,
  Sector,
  ConflictInfo,
  DATE_TYPE_LABELS,
  STATUS_LABELS,
  STATUS_COLORS,
  TIER_LABELS
} from '../../services/studio-bookings';
import { RoomWithAvailability } from '../../services/rooms';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmModal } from '../common/ConfirmModal';
import BookingTrainees from './BookingTrainees';
import BookingEmails from './BookingEmails';
import { BookingPackButton } from './BookingPackButton';
import { useFacilitatorAvailabilityCheck, availabilitySuffix, availabilityLabel, FacilitatorShortfallBanner } from './FacilitatorAvailabilityWarning';

const STATUS_DOT: Record<BookingStatus, string> = {
  pending:   'bg-amber-400',
  approved:  'bg-green-500',
  rejected:  'bg-red-500',
  cancelled: 'bg-slate-400'
};

export function StatusBadge({ status }: { status: BookingStatus }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${STATUS_COLORS[status]}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[status]}`} />
      {STATUS_LABELS[status]}
    </span>
  );
}

export const EXTERNAL_CHIP_CLASS = 'bg-slate-50 text-slate-400 border border-dashed border-slate-300';

// The requester booked over a meeting already in the room's Outlook calendar.
function OutlookConflictOverrideBadge({ booking }: { booking: StudioBooking }) {
  if (!booking.outlookConflictOverride) return null;
  return (
    <span
      title={`${booking.outlookConflictOverride.overriddenByName} chose to book over an existing Outlook meeting in this slot`}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border text-amber-700 bg-amber-50 border-amber-200"
    >
      <AlertCircle className="w-3 h-3" /> Overlaps Outlook meeting
    </span>
  );
}

// Orthogonal to BookingStatus — a booking can be approved/cancelled in our
// system but not actually reflected in Outlook if the Graph write failed.
function OutlookSyncBadge({ booking, canRetry, onRetry, retrying }: {
  booking: StudioBooking; canRetry: boolean; onRetry: () => void; retrying: boolean;
}) {
  if (booking.outlookSyncStatus !== 'failed') return null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        title={booking.outlookSyncError || 'Outlook sync failed'}
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border text-amber-700 bg-amber-50 border-amber-200"
      >
        <AlertCircle className="w-3 h-3" /> Not synced to Outlook
      </span>
      {canRetry && (
        <button
          onClick={onRetry}
          disabled={retrying}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border border-slate-200 text-slate-500 hover:bg-slate-100 disabled:opacity-60 transition-colors"
        >
          <RefreshCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} /> {retrying ? 'Retrying…' : 'Retry sync'}
        </button>
      )}
    </span>
  );
}

// ============================================================================
// Booking detail modal
// ============================================================================

export function BookingModal({ booking: initialBooking, rooms, sectors, onClose, onUpdated }: {
  booking: StudioBooking; rooms: RoomWithAvailability[]; sectors: Sector[]; onClose: () => void; onUpdated: (booking: StudioBooking) => void;
}) {
  const { user, can } = useAuth();
  const [booking, setBooking] = useState(initialBooking);

  // Keep both the modal's own view and the parent's calendar grid in sync —
  // without this, edits/cancels only show up in the grid after a page reload.
  function applyUpdate(updated: StudioBooking) {
    setBooking(updated);
    onUpdated(updated);
  }
  const [editingCounts, setEditingCounts] = useState(false);
  const [customers, setCustomers] = useState(initialBooking.numberOfCustomers?.toString() ?? '');
  const [attendees, setAttendees] = useState(initialBooking.numberOfAttendees?.toString() ?? '');
  const [saving, setSaving] = useState(false);

  const [editingFacilitator, setEditingFacilitator] = useState(false);
  const [editingSecondary, setEditingSecondary] = useState(false);
  const [editingTeamLead, setEditingTeamLead] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(false);
  const [editingDate, setEditingDate] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [editingSector, setEditingSector] = useState(false);
  const [editingRoom, setEditingRoom] = useState(false);
  const [roomId, setRoomId] = useState(booking.roomId);
  const [facilitators, setFacilitators] = useState<FacilitatorSummary[]>([]);
  const [facilitatorId, setFacilitatorId] = useState(booking.facilitatorId);
  const [secondaryFacilitatorIds, setSecondaryFacilitatorIds] = useState<string[]>(booking.secondaryFacilitatorIds ?? []);
  const [accountTeamLeadEmail, setAccountTeamLeadEmail] = useState(booking.accountTeamLeadEmail ?? '');
  const [customerName, setCustomerName] = useState(booking.customer);
  const [startDate, setStartDate] = useState(booking.startDate);
  const [endDate, setEndDate] = useState(booking.endDate);
  const [goal, setGoal] = useState(booking.goal);
  const [sector, setSector] = useState(booking.sector ?? '');
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [detailsConflicts, setDetailsConflicts] = useState<ConflictInfo[]>([]);
  const [detailsOutlookConflict, setDetailsOutlookConflict] = useState<Parameters<typeof updateBookingDetails>[1] | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [retryingSync, setRetryingSync] = useState(false);

  const isTier1OrAdmin = can('Studio::ReviewBookings');
  const isOwner = !!user && (user.userId === booking.requestedBy || user.userId === booking.facilitatorId);
  const canEdit = (isOwner || isTier1OrAdmin) && ['pending', 'approved'].includes(booking.status);
  const canCancel = (isTier1OrAdmin || isOwner) && ['pending', 'approved'].includes(booking.status);
  const canReview = isTier1OrAdmin && booking.status === 'pending';
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [reviewAction, setReviewAction] = useState<'approved' | 'rejected' | null>(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);
  const [outlookConflict, setOutlookConflict] = useState<string | null>(null);

  const editingSchedule = editingRoom || editingDate || editingFacilitator || editingSecondary;
  const availabilityCheck = useFacilitatorAvailabilityCheck({
    roomId,
    startDate,
    endDate: booking.dateType === 'multi_day' ? endDate : startDate,
    dateType: booking.dateType,
    excludeBookingId: booking.bookingId,
    enabled: editingSchedule
  });

  function openReview(action: 'approved' | 'rejected') {
    setReviewAction(action);
    setReviewNotes('');
    setOutlookConflict(null);
    setDetailsError(null);
  }

  function closeReview() {
    setReviewAction(null);
    setReviewNotes('');
    setOutlookConflict(null);
  }

  async function submitReview(forceWithoutOutlookSync = false) {
    if (!reviewAction) return;
    if (reviewAction === 'rejected' && !reviewNotes.trim()) {
      setDetailsError('Please provide a reason for rejection.');
      return;
    }
    setReviewing(true);
    try {
      const updated = await reviewBooking(booking.bookingId, reviewAction, reviewNotes.trim() || undefined, forceWithoutOutlookSync);
      applyUpdate(updated);
      closeReview();
    } catch (err: any) {
      if (err.response?.status === 424 && err.response?.data?.outlookSyncFailed) {
        setOutlookConflict(err.response.data.error || 'Could not connect to Outlook.');
      } else {
        setDetailsError(err.response?.data?.error || 'Failed to submit review');
      }
    } finally {
      setReviewing(false);
    }
  }

  async function handleCancel() {
    setCancelling(true);
    try {
      const updated = await cancelBooking(booking.bookingId);
      applyUpdate(updated);
      setShowCancelConfirm(false);
    } catch (err: any) {
      setDetailsError(err.response?.data?.error || 'Failed to cancel booking');
      setShowCancelConfirm(false);
    } finally {
      setCancelling(false);
    }
  }

  async function handleRetrySync() {
    setRetryingSync(true);
    try {
      const updated = await retryOutlookSync(booking.bookingId);
      applyUpdate(updated);
    } catch (err: any) {
      setDetailsError(err.response?.data?.error || 'Failed to retry Outlook sync');
    } finally {
      setRetryingSync(false);
    }
  }

  useEffect(() => {
    if ((editingFacilitator || editingSecondary) && facilitators.length === 0) {
      listFacilitators().then(f => setFacilitators(f.filter(x => !x.isOffboarded))).catch(() => {});
    }
  }, [editingFacilitator, editingSecondary, facilitators.length]);

  async function saveDetails(updates: {
    roomId?: string;
    facilitatorId?: string;
    secondaryFacilitatorIds?: string[];
    accountTeamLeadEmail?: string;
    sendAccountManagerEmails?: boolean;
    customer?: string;
    sector?: string;
    startDate?: string;
    endDate?: string;
    goal?: string;
    overrideOutlookConflict?: boolean;
  }) {
    setSaving(true);
    setDetailsError(null);
    setDetailsConflicts([]);
    setDetailsOutlookConflict(null);
    try {
      const updated = await updateBookingDetails(booking.bookingId, updates);
      applyUpdate(updated);
      setEditingFacilitator(false);
      setEditingSecondary(false);
      setEditingTeamLead(false);
      setEditingCustomer(false);
      setEditingDate(false);
      setEditingGoal(false);
      setEditingSector(false);
      setEditingRoom(false);
    } catch (err: any) {
      if (err.response?.status === 409 && err.response?.data?.conflicts) {
        setDetailsConflicts(err.response.data.conflicts);
        setDetailsError(err.response.data.error);
      } else if (err.response?.status === 409 && err.response?.data?.outlookConflict) {
        setDetailsOutlookConflict(updates);
        setDetailsError(err.response.data.error);
      } else {
        setDetailsError(err.response?.data?.error || 'Failed to save changes');
      }
    } finally {
      setSaving(false);
    }
  }

  function saveFacilitator() {
    saveDetails({ facilitatorId });
  }

  function toggleSecondaryFacilitator(userId: string) {
    setSecondaryFacilitatorIds(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  }

  function saveSecondaryFacilitators() {
    saveDetails({ secondaryFacilitatorIds });
  }

  function saveTeamLeadEmail() {
    saveDetails({ accountTeamLeadEmail: accountTeamLeadEmail.trim() });
  }

  function saveCustomer() {
    if (!customerName.trim()) { setDetailsError('Customer name cannot be empty'); return; }
    saveDetails({ customer: customerName.trim() });
  }

  function saveDate() {
    const nextEndDate = booking.dateType === 'multi_day' ? endDate : startDate;
    saveDetails({ startDate, endDate: nextEndDate });
  }

  function saveGoal() {
    saveDetails({ goal });
  }

  function saveSector() {
    saveDetails({ sector });
  }

  function saveRoom() {
    if (roomId === booking.roomId) { setEditingRoom(false); return; }
    saveDetails({ roomId });
  }

  let customerStatusLabel: string;
  if (booking.customerStatus === 'internal') customerStatusLabel = 'Internal';
  else if (booking.customerStatus === 'new') customerStatusLabel = 'New customer';
  else customerStatusLabel = 'Existing customer';

  let billingLabel: string;
  if (booking.billingType === 'investment') billingLabel = 'Investment';
  else if (booking.billingType === 'na') billingLabel = 'N/A';
  else billingLabel = 'Chargeable';

  const customerCountUnit = booking.numberOfCustomers === 1 ? 'customer' : 'customers';
  const customerCountLabel = booking.numberOfCustomers !== undefined
    ? `${booking.numberOfCustomers} ${customerCountUnit}`
    : null;
  const attendeeCountUnit = booking.numberOfAttendees === 1 ? 'attendee' : 'attendees';
  const attendeeCountLabel = booking.numberOfAttendees !== undefined
    ? `${booking.numberOfAttendees} ${attendeeCountUnit}`
    : null;
  const countsLabel = [customerCountLabel, attendeeCountLabel].filter(Boolean).join(' · ');

  async function saveCounts() {
    setSaving(true);
    try {
      const updates: { numberOfCustomers?: number; numberOfAttendees?: number } = {};
      if (customers !== '') updates.numberOfCustomers = Number(customers);
      if (attendees !== '') updates.numberOfAttendees = Number(attendees);
      const updated = await updateBookingMetadata(booking.bookingId, updates);
      applyUpdate(updated);
      setEditingCounts(false);
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg border border-[#e2e8f0]"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-[#e2e8f0]">
          <div className="flex items-center gap-3 flex-wrap">
            <StatusBadge status={booking.status} />
            <OutlookSyncBadge booking={booking} canRetry={isTier1OrAdmin} onRetry={handleRetrySync} retrying={retryingSync} />
            <OutlookConflictOverrideBadge booking={booking} />
            {editingCustomer ? (
              <div className="flex items-center gap-1.5">
                <input
                  autoFocus
                  type="text"
                  value={customerName}
                  onChange={e => setCustomerName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveCustomer(); else if (e.key === 'Escape') { setEditingCustomer(false); setCustomerName(booking.customer); } }}
                  className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm font-bold text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button onClick={saveCustomer} disabled={saving} className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60">
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button onClick={() => { setEditingCustomer(false); setCustomerName(booking.customer); }} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-1.5">
                <h2 className="font-bold text-navy">{booking.customer}</h2>
                {canEdit && (
                  <button onClick={() => setEditingCustomer(true)} className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50">
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-50">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {detailsError && (
            <div className="p-3 rounded-xl bg-red-50 border border-red-200 flex gap-2.5">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-700">{detailsError}</p>
                {detailsConflicts.length > 0 && (
                  <ul className="mt-1.5 text-xs text-red-600 space-y-1">
                    {detailsConflicts.map(c => (
                      <li key={c.bookingId}>
                        {c.customer} — {DATE_TYPE_LABELS[c.dateType]} on {c.startDate}
                        {c.startDate !== c.endDate ? ` to ${c.endDate}` : ''} ({c.status})
                      </li>
                    ))}
                  </ul>
                )}
                {detailsOutlookConflict && (
                  <button
                    onClick={() => saveDetails({ ...detailsOutlookConflict, overrideOutlookConflict: true })}
                    disabled={saving}
                    className="mt-2 px-2.5 py-1 text-xs font-medium rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-100 disabled:opacity-50"
                  >
                    Save anyway
                  </button>
                )}
              </div>
            </div>
          )}
          {/* Room — inline editable; moving updates the Outlook event in place */}
          <div className="flex gap-2.5 text-sm items-start">
            <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingRoom ? (
              <div className="flex items-center gap-2 flex-1">
                <select
                  autoFocus
                  value={roomId}
                  onChange={e => setRoomId(e.target.value)}
                  className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                >
                  {rooms.map(r => (
                    <option key={r.roomId} value={r.roomId}>{r.name}</option>
                  ))}
                  {!rooms.some(r => r.roomId === booking.roomId) && (
                    <option value={booking.roomId}>{booking.roomName}</option>
                  )}
                </select>
                <button
                  onClick={saveRoom}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingRoom(false); setRoomId(booking.roomId); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">{booking.roomName} · {DATE_TYPE_LABELS[booking.dateType]}</span>
                {canEdit && (
                  <button
                    onClick={() => setEditingRoom(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Date — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Clock className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingDate ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="flex items-center gap-2 flex-1">
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                  />
                  {booking.dateType === 'multi_day' && (
                    <>
                      <span className="text-slate-400">→</span>
                      <input
                        type="date"
                        min={startDate}
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        className="border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                      />
                    </>
                  )}
                </div>
                <button
                  onClick={saveDate}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingDate(false); setStartDate(booking.startDate); setEndDate(booking.endDate); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {booking.startDate === booking.endDate
                    ? booking.startDate
                    : `${booking.startDate} → ${booking.endDate}`}
                  {' · '}{booking.startTime}–{booking.endTime}
                </span>
                {canEdit && (
                  <button
                    onClick={() => setEditingDate(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {(editingRoom || editingDate) && (
            <FacilitatorShortfallBanner check={availabilityCheck} roomName={rooms.find(r => r.roomId === roomId)?.name} />
          )}

          {/* Facilitator — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <User className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingFacilitator ? (
              <div className="flex items-center gap-2 flex-1">
                <select
                  value={facilitatorId}
                  onChange={e => setFacilitatorId(e.target.value)}
                  className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                >
                  {facilitators.length === 0 && (
                    <option value={facilitatorId}>{booking.facilitatorName}</option>
                  )}
                  {facilitators.map(f => (
                    <option key={f.userId} value={f.userId}>
                      {f.displayName} — {TIER_LABELS[f.tier]}{availabilitySuffix(availabilityCheck, f.userId)}
                    </option>
                  ))}
                </select>
                <button
                  onClick={saveFacilitator}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingFacilitator(false); setFacilitatorId(booking.facilitatorId); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {booking.facilitatorName}
                  <span className="ml-1.5 text-xs text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-full">
                    {TIER_LABELS[booking.facilitatorTier]}
                  </span>
                </span>
                {canEdit && (
                  <button
                    onClick={() => setEditingFacilitator(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Secondary facilitators — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Users className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingSecondary ? (
              <div className="flex flex-col gap-2 flex-1">
                <div className="flex flex-col gap-1 max-h-40 overflow-y-auto border border-[#e2e8f0] rounded-lg p-2">
                  {facilitators.filter(f => f.userId !== facilitatorId).map(f => (
                    <label key={f.userId} className="flex items-center gap-2 px-1.5 py-1 rounded-lg hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={secondaryFacilitatorIds.includes(f.userId)}
                        onChange={() => toggleSecondaryFacilitator(f.userId)}
                        className="w-3.5 h-3.5 accent-primary rounded"
                      />
                      <span className="text-sm text-navy">{f.displayName}</span>
                      <span className="text-xs text-slate-400">{TIER_LABELS[f.tier]}</span>
                      {availabilityLabel(availabilityCheck, f.userId) && (
                        <span className="text-xs font-semibold text-amber-600">{availabilityLabel(availabilityCheck, f.userId)}</span>
                      )}
                    </label>
                  ))}
                </div>
                <div className="flex items-center gap-2 self-end">
                  <button
                    onClick={saveSecondaryFacilitators}
                    disabled={saving}
                    className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                  >
                    <Check className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => { setEditingSecondary(false); setSecondaryFacilitatorIds(booking.secondaryFacilitatorIds ?? []); }}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1 flex-wrap">
                {booking.secondaryFacilitatorNames && booking.secondaryFacilitatorNames.length > 0 ? (
                  booking.secondaryFacilitatorNames.map(name => (
                    <span key={name} className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">{name}</span>
                  ))
                ) : (
                  <span className="text-slate-400 italic text-sm">No secondary facilitators</span>
                )}
                {canEdit && (
                  <button
                    onClick={() => setEditingSecondary(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Trainees — facilitator training bookings only */}
          <BookingTrainees
            booking={booking}
            canEdit={canEdit}
            onUpdated={applyUpdate}
            onError={setDetailsError}
          />

          {/* Account team lead email — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Mail className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingTeamLead ? (
              <div className="flex items-center gap-2 flex-1">
                <input
                  type="email"
                  placeholder="e.g. jane.doe@version1.com"
                  value={accountTeamLeadEmail}
                  onChange={e => setAccountTeamLeadEmail(e.target.value)}
                  className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
                <button
                  onClick={saveTeamLeadEmail}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingTeamLead(false); setAccountTeamLeadEmail(booking.accountTeamLeadEmail ?? ''); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {booking.accountTeamLeadEmail || <span className="text-slate-400 italic">No account team lead set</span>}
                </span>
                {canEdit && (
                  <button
                    onClick={() => setEditingTeamLead(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          <BookingPackButton booking={booking} />

          <BookingEmails
            bookingId={booking.bookingId}
            hasAccountManager={!!booking.accountTeamLeadEmail}
            enabled={!!booking.sendAccountManagerEmails}
            canEdit={canEdit}
            canRetry={isTier1OrAdmin}
            saving={saving}
            refreshKey={[booking.status, booking.startDate, booking.endDate, booking.roomId, booking.facilitatorId, booking.sendAccountManagerEmails].join('|')}
            onToggle={value => saveDetails({ sendAccountManagerEmails: value })}
          />

          <div className="flex gap-2.5 text-sm">
            <Tag className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{booking.eventTypeName}</span>
          </div>
          <div className="flex gap-2.5 text-sm">
            <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            <span className="text-navy">{customerStatusLabel} · {billingLabel}</span>
          </div>

          {/* Sector — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Tag className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingSector ? (
              <div className="flex items-center gap-2 flex-1">
                <select
                  autoFocus
                  value={sector}
                  onChange={e => setSector(e.target.value)}
                  className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
                >
                  <option value="">None</option>
                  {sectors.map(s => (
                    <option key={s.sectorId} value={s.name}>{s.name}</option>
                  ))}
                  {booking.sector && !sectors.some(s => s.name === booking.sector) && (
                    <option value={booking.sector}>{booking.sector} (inactive)</option>
                  )}
                </select>
                <button
                  onClick={saveSector}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingSector(false); setSector(booking.sector ?? ''); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {booking.sector || <span className="text-slate-400 italic">No sector set</span>}
                </span>
                {canEdit && (
                  <button
                    onClick={() => setEditingSector(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Attendee counts — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Users className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingCounts ? (
              <div className="flex items-center gap-2 flex-1">
                <div className="flex flex-col gap-1 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Customers</span>
                    <input
                      type="number"
                      min={0}
                      value={customers}
                      onChange={e => setCustomers(e.target.value)}
                      className="w-20 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500 w-24">Attendees</span>
                    <input
                      type="number"
                      min={0}
                      value={attendees}
                      onChange={e => setAttendees(e.target.value)}
                      className="w-20 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                    />
                  </div>
                </div>
                <button
                  onClick={saveCounts}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => setEditingCounts(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2 flex-1">
                <span className="text-navy">
                  {countsLabel || <span className="text-slate-400 italic">No counts set</span>}
                </span>
                <button
                  onClick={() => setEditingCounts(true)}
                  className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Goal / description — inline editable */}
          <div className="flex gap-2.5 text-sm items-start">
            <Target className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
            {editingGoal ? (
              <div className="flex items-start gap-2 flex-1">
                <textarea
                  rows={3}
                  maxLength={500}
                  value={goal}
                  onChange={e => setGoal(e.target.value)}
                  className="flex-1 border border-[#e2e8f0] rounded-lg px-2 py-1 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
                />
                <button
                  onClick={saveGoal}
                  disabled={saving}
                  className="p-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-60"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => { setEditingGoal(false); setGoal(booking.goal); }}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="flex items-start gap-2 flex-1">
                <span className="text-navy">{booking.goal}</span>
                {canEdit && (
                  <button
                    onClick={() => setEditingGoal(true)}
                    className="ml-auto p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-50 flex-shrink-0"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            )}
          </div>
          {booking.reviewNotes && (
            <div className="p-3 rounded-xl bg-slate-50 border border-[#e2e8f0] mt-2">
              <p className="text-xs font-semibold text-slate-500 mb-1 uppercase tracking-wide">Review notes</p>
              <p className="text-sm text-navy">{booking.reviewNotes}</p>
              {booking.reviewedByName && (
                <p className="text-xs text-slate-400 mt-1">— {booking.reviewedByName}</p>
              )}
            </div>
          )}

          {/* Tier 1 / admin approve/reject, inline */}
          {canReview && outlookConflict && (
            <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 mt-2">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800">Couldn't connect to Outlook</p>
                  <p className="text-sm text-amber-700 mt-0.5">{outlookConflict}</p>
                  <p className="text-xs text-amber-700 mt-1">
                    You can approve anyway — the room won't be held in Outlook until sync is retried.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={closeReview}
                  className="flex-1 py-2 border border-[#e2e8f0] rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => submitReview(true)}
                  disabled={reviewing}
                  className="flex-1 py-2 rounded-lg text-xs font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-60"
                >
                  {reviewing ? 'Approving…' : 'Approve anyway'}
                </button>
              </div>
            </div>
          )}
          {canReview && reviewAction && !outlookConflict && (
            <div className="p-3 rounded-xl bg-slate-50 border border-[#e2e8f0] mt-2">
              <label className="block text-xs font-semibold text-navy mb-1.5">
                Notes {reviewAction === 'approved'
                  ? <span className="text-slate-400 font-normal">(optional)</span>
                  : <span className="text-red-500">*</span>}
              </label>
              <textarea
                autoFocus
                rows={2}
                value={reviewNotes}
                onChange={e => setReviewNotes(e.target.value)}
                placeholder={reviewAction === 'approved' ? 'Any notes for the requester…' : 'Reason for rejection…'}
                className="w-full border border-[#e2e8f0] rounded-lg px-2 py-1.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
              />
              <div className="flex gap-2 mt-2">
                <button
                  onClick={closeReview}
                  className="flex-1 py-1.5 border border-[#e2e8f0] rounded-lg text-xs font-semibold text-slate-500 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => submitReview(false)}
                  disabled={reviewing}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-bold text-white disabled:opacity-60 ${
                    reviewAction === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {reviewing ? 'Submitting…' : reviewAction === 'approved' ? 'Approve' : 'Reject'}
                </button>
              </div>
            </div>
          )}
          {canReview && !reviewAction && (
            <div className="flex gap-2 pt-1">
              <button
                onClick={() => openReview('approved')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-green-50 border border-green-200 text-green-700 text-xs font-semibold hover:bg-green-100 transition-colors"
              >
                <Check className="w-3.5 h-3.5" /> Approve
              </button>
              <button
                onClick={() => openReview('rejected')}
                className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-100 transition-colors"
              >
                <X className="w-3.5 h-3.5" /> Reject
              </button>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <p className="text-xs text-slate-400">
              Requested by {booking.requestedByName}
            </p>
            {canCancel && (
              <button
                onClick={() => setShowCancelConfirm(true)}
                disabled={cancelling}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-xs font-semibold hover:bg-red-100 disabled:opacity-60 transition-colors"
              >
                <Trash2 className="w-3.5 h-3.5" /> {cancelling ? 'Cancelling…' : 'Cancel booking'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
    <ConfirmModal
      isOpen={showCancelConfirm}
      onClose={() => setShowCancelConfirm(false)}
      onConfirm={handleCancel}
      title="Cancel booking"
      message={`Cancel this booking for ${booking.customer}? This removes it from Outlook if it was synced.`}
      confirmText="Cancel booking"
      cancelText="Keep booking"
      variant="danger"
      isProcessing={cancelling}
    />
    </>
  );
}
